/**
 * Public API contracts for flow fields (QL-300).
 *
 *   GET /api/v1/flows/{id}/fields                 → FlowFieldDefinitionsResponse
 *   GET /api/v1/flow-instances/{id}/fields        → FlowInstanceFieldsResponse
 *   PUT /api/v1/flow-instances/{id}/fields        → UpdateFlowInstanceFieldsRequest
 *                                                   / FlowInstanceFieldsResponse
 *
 * A flow field has a scope: `global` fields hold one value per instance, `goal`
 * fields one per goal and `portfolio` fields one per portfolio. A scoped value
 * is written with the matching `goalId` / `portfolioId` and read back through
 * the same dimension on the query string.
 */

export const FLOW_FIELD_SCOPES = ["global", "goal", "portfolio"] as const;
export type FlowFieldScope = (typeof FLOW_FIELD_SCOPES)[number];

// ─── GET /flows/{id}/fields ─────────────────────────────────────────────────

export type FlowFieldDefinitionOption = {
  optionId: string;
  label: string;
  order: number;
  /**
   * Set by the tenant in the flow editor; written as `valueSyncKey` (not
   * `value`) and read back as `valueSyncKey`. Unique within the field.
   * Omitted when the option has none.
   */
  syncKey?: string;
};

export type FlowFieldDefinition = {
  id: string;
  name: string;
  type: string;
  required: boolean;
  scope: FlowFieldScope;
  order: number;
  /**
   * QL-422 — set by the tenant in the flow editor; usable as `flowFieldSyncKey`
   * when writing values. Case-sensitive exact match, unique within the flow.
   * Omitted when the field has none.
   */
  syncKey?: string;
  /** Only on option-based fields (dropdown/radio/multiselect); archived options are omitted. */
  options?: FlowFieldDefinitionOption[];
};

export type FlowFieldDefinitionsResponse = {
  flowId: string;
  flowName: string;
  fields: FlowFieldDefinition[];
};

// ─── GET|PUT /flow-instances/{id}/fields ────────────────────────────────────

/**
 * Which goal / portfolio dimension a fields read resolves scoped values for.
 * Both may be given at once; a scoped field whose dimension is absent reads as
 * `value: null`. Global fields ignore the dimension.
 */
export type FlowInstanceFieldsQuery = {
  goalId?: string;
  portfolioId?: string;
};

export type FlowInstanceFieldView = {
  id: string;
  name: string;
  type: string;
  required: boolean;
  scope: FlowFieldScope;
  /** QL-422 — the field's sync key; omitted when it has none. */
  syncKey?: string;
  /** Option-based fields store option ids (multiselect: a JSON array of ids). */
  value: string | null;
  /** Option ids resolved to their labels; equal to `value` for other types. */
  valueLabel: string | null;
  /**
   * Option fields only: the selected option's sync key (multiselect: a JSON
   * array of keys, like `value`). Null when there is no value, or a selected
   * option has no sync key or has been removed from the field (for
   * multiselect the whole list, never a partial one); omitted for other field
   * types.
   */
  valueSyncKey?: string | null;
};

export type FlowInstanceFieldsResponse = {
  flowInstanceId: string;
  fields: FlowInstanceFieldView[];
};

/**
 * One value to write. `goalId` is required for (and only for) a `goal`-scoped
 * field, `portfolioId` for a `portfolio`-scoped one; a `global` field takes
 * neither. The goal / portfolio must belong to the instance.
 *
 * QL-422 — the field is named by exactly one of `flowFieldId` or
 * `flowFieldSyncKey` (the key set on the field in the flow editor).
 */
export type FlowInstanceFieldValueInput = {
  flowFieldId?: string;
  flowFieldSyncKey?: string;
  /**
   * Exactly one of `value` and `valueSyncKey`. `value` takes an option id or
   * label on option fields; `valueSyncKey` takes the option's sync key
   * (multiselect: a JSON array of keys) and only exists for option fields.
   */
  value?: string;
  valueSyncKey?: string;
  goalId?: string;
  portfolioId?: string;
};

export type UpdateFlowInstanceFieldsRequest = {
  values: FlowInstanceFieldValueInput[];
};
